import { useMemo, useState } from 'react';
import { format, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Landmark,
  Wallet,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  ArrowLeftRight,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useFinance } from '@/contexts/FinanceContext';
import { MarkAsPaidDialog } from '@/features/transactions/MarkAsPaidDialog';
import type { FinanceTransaction } from '@/types/finance';

const fmtMoney = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

export function ReconciliacaoPage() {
  const { accounts, transactions } = useFinance();
  const activeAccounts = useMemo(() => accounts.filter((a) => a.isActive), [accounts]);

  const today = new Date();
  const [accountId, setAccountId] = useState(activeAccounts[0]?.id ?? '');
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-indexed
  const [paymentDialogTx, setPaymentDialogTx] = useState<FinanceTransaction | null>(null);

  const account = accounts.find((a) => a.id === accountId);

  const range = useMemo(() => {
    const ref = new Date(year, month, 1);
    return { start: startOfMonth(ref), end: endOfMonth(ref) };
  }, [year, month]);

  // Conjuntos: transações do extrato (com externalId, completed) e do sistema (sem externalId).
  const { bankRows, systemRows, systemMatchableAmounts } = useMemo(() => {
    const inAccount = transactions.filter(
      (t) => t.accountId === accountId || t.destinationAccountId === accountId,
    );

    const inRange = inAccount.filter((t) => {
      const refDate = t.paymentDate ?? t.dueDate;
      return isWithinInterval(refDate, range);
    });

    const bank: FinanceTransaction[] = [];
    const system: FinanceTransaction[] = [];
    for (const t of inRange) {
      if (t.externalId) bank.push(t);
      else system.push(t);
    }

    bank.sort((a, b) => {
      const da = a.paymentDate ?? a.dueDate;
      const db = b.paymentDate ?? b.dueDate;
      return db.getTime() - da.getTime();
    });
    system.sort((a, b) => {
      const da = a.paymentDate ?? a.dueDate;
      const db = b.paymentDate ?? b.dueDate;
      return db.getTime() - da.getTime();
    });

    // Soma simples por valor absoluto + tipo para sugerir matches
    const matchable = new Set(
      bank.map((b) => `${b.type}:${Math.round(b.amount * 100)}:${(b.paymentDate ?? b.dueDate).toISOString().slice(0, 10)}`),
    );
    return { bankRows: bank, systemRows: system, systemMatchableAmounts: matchable };
  }, [transactions, accountId, range]);

  const bankInflow = bankRows
    .filter((t) => t.type === 'income' || (t.type === 'transfer' && t.destinationAccountId === accountId))
    .reduce((s, t) => s + t.amount, 0);
  const bankOutflow = bankRows
    .filter((t) => t.type === 'expense' || (t.type === 'transfer' && t.accountId === accountId))
    .reduce((s, t) => s + t.amount, 0);
  const bankNet = bankInflow - bankOutflow;

  const systemPendingIncome = systemRows
    .filter((t) => t.status === 'pending' && t.type === 'income')
    .reduce((s, t) => s + t.amount, 0);
  const systemPendingExpense = systemRows
    .filter((t) => t.status === 'pending' && t.type === 'expense')
    .reduce((s, t) => s + t.amount, 0);

  const handleMarkPaid = (t: FinanceTransaction) => {
    setPaymentDialogTx(t);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-brand text-2xl font-bold text-foreground">Conciliação bancária</h1>
        <p className="text-sm text-muted-foreground">
          Compare o extrato do banco com os lançamentos do sistema. Marque pendências como pagas
          conforme o extrato confirma.
        </p>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-white p-4">
        <div className="flex-1 min-w-[200px]">
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Conta</label>
          <Select value={accountId} onValueChange={setAccountId}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione a conta" />
            </SelectTrigger>
            <SelectContent>
              {activeAccounts.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Mês</label>
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTH_NAMES.map((m, i) => (
                <SelectItem key={i} value={String(i)}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-28">
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Ano</label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[year - 2, year - 1, year, year + 1].map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Wallet size={14} /> Saldo atual
          </div>
          <div className="mt-1 font-brand text-xl font-bold text-primary tabular-nums">
            {fmtMoney(account?.currentBalance ?? 0)}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Landmark size={14} /> Extrato no período
          </div>
          <div
            className={`mt-1 font-brand text-xl font-bold tabular-nums ${bankNet >= 0 ? 'text-income' : 'text-expense'}`}
          >
            {fmtMoney(bankNet)}
          </div>
          <div className="text-[11px] text-muted-foreground">
            <span className="text-income">+ {fmtMoney(bankInflow)}</span> /{' '}
            <span className="text-expense">− {fmtMoney(bankOutflow)}</span>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <TrendingDown size={14} /> Pendentes a pagar
          </div>
          <div className="mt-1 font-brand text-xl font-bold text-expense tabular-nums">
            {fmtMoney(systemPendingExpense)}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <TrendingUp size={14} /> Pendentes a receber
          </div>
          <div className="mt-1 font-brand text-xl font-bold text-income tabular-nums">
            {fmtMoney(systemPendingIncome)}
          </div>
        </div>
      </div>

      {/* 2 colunas lado a lado */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Coluna Banco */}
        <section className="rounded-xl border border-border bg-white">
          <header className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <Landmark size={16} className="text-primary" />
              <h2 className="font-brand font-semibold text-foreground">Extrato bancário</h2>
            </div>
            <Badge variant="outline" className="text-xs">
              {bankRows.length} {bankRows.length === 1 ? 'lançamento' : 'lançamentos'}
            </Badge>
          </header>
          <div className="max-h-[60vh] overflow-y-auto">
            {bankRows.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                Nenhum extrato importado nesse período. Importe um OFX em Transações → Importar.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {bankRows.map((t) => {
                  const refDate = t.paymentDate ?? t.dueDate;
                  const isTransfer = t.type === 'transfer';
                  const isInflow =
                    t.type === 'income' || (isTransfer && t.destinationAccountId === accountId);
                  return (
                    <li key={t.id} className="flex items-start gap-3 px-4 py-3">
                      <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-income" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="truncate text-sm font-medium text-foreground">
                            {t.description}
                          </span>
                          <span
                            className={`shrink-0 text-sm font-semibold tabular-nums ${isInflow ? 'text-income' : 'text-expense'}`}
                          >
                            {isInflow ? '+' : '−'} {fmtMoney(t.amount)}
                          </span>
                        </div>
                        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                          <span>{format(refDate, "dd/MM/yyyy", { locale: ptBR })}</span>
                          {isTransfer && (
                            <span className="inline-flex items-center gap-0.5">
                              <ArrowLeftRight size={11} /> Transferência
                            </span>
                          )}
                          {t.externalId && (
                            <span className="font-mono">FITID: {t.externalId.slice(0, 12)}</span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>

        {/* Coluna Sistema */}
        <section className="rounded-xl border border-border bg-white">
          <header className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <Wallet size={16} className="text-primary" />
              <h2 className="font-brand font-semibold text-foreground">Sistema</h2>
            </div>
            <Badge variant="outline" className="text-xs">
              {systemRows.length} {systemRows.length === 1 ? 'lançamento' : 'lançamentos'}
            </Badge>
          </header>
          <div className="max-h-[60vh] overflow-y-auto">
            {systemRows.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                Nenhum lançamento manual no sistema nesse período.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {systemRows.map((t) => {
                  const refDate = t.paymentDate ?? t.dueDate;
                  const isPending = t.status === 'pending';
                  const sameAmountInBank = systemMatchableAmounts.has(
                    `${t.type}:${Math.round(t.amount * 100)}:${refDate.toISOString().slice(0, 10)}`,
                  );
                  const isTransfer = t.type === 'transfer';
                  const isInflow =
                    t.type === 'income' || (isTransfer && t.destinationAccountId === accountId);
                  return (
                    <li key={t.id} className="flex items-start gap-3 px-4 py-3">
                      {isPending ? (
                        <AlertCircle size={16} className="mt-0.5 shrink-0 text-amber-500" />
                      ) : (
                        <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-muted-foreground" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="truncate text-sm font-medium text-foreground">
                            {t.description}
                          </span>
                          <span
                            className={`shrink-0 text-sm font-semibold tabular-nums ${isInflow ? 'text-income' : 'text-expense'}`}
                          >
                            {isInflow ? '+' : '−'} {fmtMoney(t.amount)}
                          </span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                          <span>{format(refDate, "dd/MM/yyyy", { locale: ptBR })}</span>
                          {isPending && (
                            <span className="rounded bg-amber-50 px-1.5 py-0.5 font-medium text-amber-700">
                              Pendente
                            </span>
                          )}
                          {sameAmountInBank && isPending && (
                            <span className="rounded bg-sky-50 px-1.5 py-0.5 font-medium text-sky-700">
                              Match no extrato
                            </span>
                          )}
                          {isTransfer && (
                            <span className="inline-flex items-center gap-0.5">
                              <ArrowLeftRight size={11} /> Transferência
                            </span>
                          )}
                        </div>
                        {isPending && (
                          <div className="mt-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => handleMarkPaid(t)}
                            >
                              <CheckCircle2 size={12} className="mr-1" /> Marcar como{' '}
                              {t.type === 'income' ? 'recebido' : 'pago'}
                            </Button>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      </div>

      <MarkAsPaidDialog
        open={!!paymentDialogTx}
        onOpenChange={(v) => {
          if (!v) setPaymentDialogTx(null);
        }}
        transaction={paymentDialogTx}
      />
    </div>
  );
}
